/**
 * Emotion-first sentiment display config — single source of truth for the
 * agent-web sentiment surfaces (per-message chip + the ticket-sidebar
 * trajectory sparkline + the AI Insights headline pill).
 *
 * Backend contract (all fields optional — degrade gracefully when absent):
 *  - per customer message: `message.metadata.sentiment = { score, dominantEmotion, confidence }`
 *  - agent-assist `analyzeTicket` → additive `TicketAnalysis.sentimentTrajectory`.
 *
 * 13 emotions, grouped by valence band:
 *   negative → red / amber tokens   (reuses TicketSidebar 'negative'/'frustrated')
 *   neutral  → grey tokens          (reuses TicketSidebar 'neutral')
 *   positive → green / teal tokens  (reuses TicketSidebar 'positive')
 *
 * The Tailwind `color`/`bgColor` tokens mirror the local `sentimentDisplayConfig`
 * that previously lived in TicketSidebar.tsx:570-593. A raw `hex` is supplied too
 * because recharts strokes need a literal color (see components/charts/LineChart.tsx).
 */

export type ValenceBand = 'negative' | 'neutral' | 'positive';

export type DominantEmotion =
  // negative
  | 'anger'
  | 'frustration'
  | 'disappointment'
  | 'anxiety'
  | 'sadness'
  | 'confusion'
  // neutral
  | 'surprise'
  | 'curiosity'
  | 'neutral'
  // positive
  | 'satisfaction'
  | 'gratitude'
  | 'joy'
  | 'excitement';

export interface EmotionDisplay {
  label: string;
  emoji: string;
  /** Tailwind text-color utility (light + dark) */
  color: string;
  /** Tailwind background utility (light + dark) */
  bgColor: string;
  valenceBand: ValenceBand;
  /** Raw hex — recharts strokes/dots need a literal color, not a Tailwind class. */
  hex: string;
}

/** A single scored customer-message reading (oldest→newest in a trajectory). */
export interface SentimentPoint {
  timestamp: string;
  score: number;
  dominantEmotion: string;
  confidence: number;
  messageId?: string;
}

/** Derived (read-time) sentiment lifecycle for a ticket; points are oldest→newest. */
export interface SentimentTrajectory {
  points: SentimentPoint[];
  current: { score: number; dominantEmotion: string; confidence: number } | null;
  initial: { score: number; dominantEmotion: string; confidence: number } | null;
}

/** Per-message sentiment payload as it arrives on `message.metadata.sentiment`. */
export interface MessageSentiment {
  score: number;
  dominantEmotion: string;
  confidence: number;
}

/**
 * Display config per emotion. Tokens reuse the palette already used by the
 * ticket sidebar so the new surfaces feel native:
 *  - red    (text-red-600 / bg-red-100)      ← was 'frustrated'
 *  - orange (text-orange-600 / bg-orange-100)← was 'negative'
 *  - amber  (text-amber-600 / bg-amber-100)
 *  - gray   (text-gray-600 / bg-gray-…)      ← was 'neutral'
 *  - green  (text-green-600 / bg-green-100)  ← was 'positive'
 *  - teal   (text-teal-600 / bg-teal-100)
 */
export const emotionDisplayConfig: Record<DominantEmotion, EmotionDisplay> = {
  // ── negative ──────────────────────────────────────────────────────────────
  anger: {
    label: 'Anger',
    emoji: '😠',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    valenceBand: 'negative',
    hex: '#dc2626', // red-600
  },
  frustration: {
    label: 'Frustration',
    emoji: '😤',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    valenceBand: 'negative',
    hex: '#ef4444', // red-500
  },
  disappointment: {
    label: 'Disappointment',
    emoji: '😞',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    valenceBand: 'negative',
    hex: '#ea580c', // orange-600
  },
  anxiety: {
    label: 'Anxiety',
    emoji: '😰',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    valenceBand: 'negative',
    hex: '#d97706', // amber-600
  },
  sadness: {
    label: 'Sadness',
    emoji: '😢',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    valenceBand: 'negative',
    hex: '#f97316', // orange-500
  },
  confusion: {
    label: 'Confusion',
    emoji: '😕',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    valenceBand: 'negative',
    hex: '#f59e0b', // amber-500
  },
  // ── neutral ───────────────────────────────────────────────────────────────
  surprise: {
    label: 'Surprise',
    emoji: '😮',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    valenceBand: 'neutral',
    hex: '#6b7280', // gray-500
  },
  curiosity: {
    label: 'Curiosity',
    emoji: '🤔',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    valenceBand: 'neutral',
    hex: '#6b7280', // gray-500
  },
  neutral: {
    label: 'Neutral',
    emoji: '😐',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    valenceBand: 'neutral',
    hex: '#9ca3af', // gray-400
  },
  // ── positive ──────────────────────────────────────────────────────────────
  satisfaction: {
    label: 'Satisfaction',
    emoji: '🙂',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    valenceBand: 'positive',
    hex: '#16a34a', // green-600
  },
  gratitude: {
    label: 'Gratitude',
    emoji: '🙏',
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-100 dark:bg-teal-900/30',
    valenceBand: 'positive',
    hex: '#0d9488', // teal-600
  },
  joy: {
    label: 'Joy',
    emoji: '😊',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    valenceBand: 'positive',
    hex: '#22c55e', // green-500
  },
  excitement: {
    label: 'Excitement',
    emoji: '🤩',
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-100 dark:bg-teal-900/30',
    valenceBand: 'positive',
    hex: '#14b8a6', // teal-500
  },
};

/** Per-band stroke color for the trajectory line (overall valence). */
export const valenceBandHex: Record<ValenceBand, string> = {
  negative: '#ef4444', // red-500
  neutral: '#9ca3af', // gray-400
  positive: '#22c55e', // green-500
};

const NEUTRAL_EMOTION: DominantEmotion = 'neutral';

/**
 * Resolve any backend `dominantEmotion` string to a known config entry.
 * Unknown / missing values fall back to `neutral` so callers never crash.
 */
export function resolveEmotion(dominantEmotion?: string | null): EmotionDisplay {
  if (!dominantEmotion) return emotionDisplayConfig[NEUTRAL_EMOTION];
  const key = dominantEmotion.toLowerCase().trim() as DominantEmotion;
  return emotionDisplayConfig[key] ?? emotionDisplayConfig[NEUTRAL_EMOTION];
}

/**
 * Adapter so the legacy 4-label sentiment (`positive | neutral | negative |
 * frustrated`, still produced by the un-upgraded backend and resolved in
 * `normalizeTicketAnalysis`) maps onto an emotion in the new config.
 */
export function legacyLabelToEmotion(label?: string | null): DominantEmotion {
  switch ((label ?? '').toLowerCase().trim()) {
    case 'positive':
      return 'satisfaction';
    case 'negative':
      return 'disappointment';
    case 'frustrated':
      return 'frustration';
    case 'neutral':
    case 'mixed':
    case '':
      return 'neutral';
    default:
      // Could already be an emotion name (e.g. backend upgraded mid-flight).
      return (label!.toLowerCase().trim() as DominantEmotion) in emotionDisplayConfig
        ? (label!.toLowerCase().trim() as DominantEmotion)
        : 'neutral';
  }
}

/**
 * Map a numeric valence score (−1..+1) to a band. Mirrors the ±0.2 thresholds
 * the ai-engine uses to derive the backward-compat 4-label sentiment.
 */
export function scoreToBand(score: number): ValenceBand {
  if (score <= -0.2) return 'negative';
  if (score >= 0.2) return 'positive';
  return 'neutral';
}
